// chatbotLangGraph.tsx — MexaScreen production chat → LangGraph POST /chat (auto token, panel sync).
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BACKEND_URL, getAccessTokenForOrchestrator, getAuthHeaders } from './backendClient';
import { onAuthStateChange } from './components/authForm';
import { applyOrchestratorChatSync } from './orchestratorSync';
import { STEP_LABELS, stepLabel, } from './langGraphChatTypes';
import { appendStepFiche, resolveStepFiche, shouldShowStepFiche, stepFicheDedupKey, } from './langGraphStepFiche';
import { appendChatNotices, noticeIntroMessage, resolveChatNotices, shouldSuppressOperationalBubble, } from './langGraphChatNotice';
import PipelineDebugDrawer from './langGraphPipelineDebug';
import { LANGGRAPH_PROJECT_ID_KEY, getActiveSessionId, syncLangGraphProjectId, updateProjectTarget, } from './sessionProjects';
const LANGGRAPH_URL = process.env.LANGGRAPH_URL || 'http://localhost:8000';
// ── INFRA: FRONTEND -> ALB --> BACKEND -> AGENTCORE (Option A) ────────────────────────────────────────────
// Dev local  : ORCHESTRATOR_VIA_BACKEND=false → POST http://localhost:8000/chat (FastAPI direct)
// Prod / test: ORCHESTRATOR_VIA_BACKEND=true  → POST {BACKEND_URL}/orchestrator/chat via ALB
const USE_BACKEND_ORCHESTRATOR_PROXY = process.env.ORCHESTRATOR_VIA_BACKEND === 'true';
const CHAT_URL = USE_BACKEND_ORCHESTRATOR_PROXY
    ? `${BACKEND_URL}/orchestrator/chat`
    : `${LANGGRAPH_URL}/chat`;
const THREAD_URL = USE_BACKEND_ORCHESTRATOR_PROXY
    ? `${BACKEND_URL}/orchestrator/chat/thread`
    : `${LANGGRAPH_URL}/chat/thread`;
/** Same placeholder as left-panel `#peptide-input` (index.html). */
const PANEL_PEPTIDE_PLACEHOLDER = 'Enter peptide sequence (e.g. ALA CYS1s GLY PRO TYR CYS1s NH2)';
function getPanelPeptidePlaceholder() {
    const panel = document.getElementById('peptide-input');
    const fromDom = ((panel === null || panel === void 0 ? void 0 : panel.placeholder) || '').trim();
    return fromDom || PANEL_PEPTIDE_PLACEHOLDER;
}
/** Same rule as peptideForm.ts: spaces → hyphens while typing. */
function attachPeptideSpaceToDash(input) {
    input.addEventListener('input', () => {
        var _a;
        const cursor = (_a = input.selectionStart) !== null && _a !== void 0 ? _a : 0;
        const value = input.value;
        const next = value.replace(/\s+/g, '-');
        if (next === value)
            return;
        input.value = next;
        const diff = next.length - value.length;
        input.setSelectionRange(cursor + diff, cursor + diff);
    });
}
/** Same normalization as left-panel submit (trim + spaces → dashes). */
function normalizePanelPeptideSequence(raw) {
    return raw.trim().replace(/\s+/g, '-');
}
/** Site chrome is always English. Chatbot UI follows backend ui_lang. */
let chatUiLang = 'en';
function resolveUiLang() {
    return 'en';
}
function chatLang(data) {
    var _a;
    return (_a = data === null || data === void 0 ? void 0 : data.ui_lang) !== null && _a !== void 0 ? _a : chatUiLang;
}
function t(_fr, en) {
    return en;
}
const CLEAR_STEP1_LABEL = STEP_LABELS.project_scoping;
// Stores the MexaScreen session number ("1", "2"…).
// Server builds LangGraph config: actor_id = Cognito sub, thread_id = sessionId.
// Same contract on SQLite (local) and AgentCoreMemorySaver (prod).
// Written by sessionProjects.ts when the user switches project in the navbar.
function getLoggedInUsername() {
    var _a;
    try {
        const raw = localStorage.getItem('currentUser');
        if (!raw)
            return null;
        return (_a = JSON.parse(raw).username) !== null && _a !== void 0 ? _a : null;
    }
    catch (_b) {
        return null;
    }
}
function resolveActiveProjectId() {
    var _a;
    const username = getLoggedInUsername();
    if (username) {
        return String(getActiveSessionId(username));
    }
    return (_a = localStorage.getItem(LANGGRAPH_PROJECT_ID_KEY)) !== null && _a !== void 0 ? _a : '1';
}
const ACTION_BANNER_ID = 'langgraph-action-banner';
function getChatHistory() {
    return document.getElementById('chat-history');
}
/** Scroll after layout — banners/fiches need a frame or the bottom stays clipped. */
function scrollChatToBottom() {
    const chatHistory = getChatHistory();
    if (!chatHistory)
        return;
    const run = () => {
        chatHistory.scrollTop = chatHistory.scrollHeight;
    };
    run();
    requestAnimationFrame(() => requestAnimationFrame(run));
}
function escapeHtml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
/** Escape then render common markdown (**bold**, *italic*, `code`) for chat bubbles. */
function formatChatMarkdown(text) {
    return escapeHtml(text)
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code>$1</code>');
}
function appendChatLine(role, text) {
    const chatHistory = getChatHistory();
    if (!chatHistory)
        return;
    const p = document.createElement('p');
    p.innerHTML = `<span class="${role}"></span>${formatChatMarkdown(text)}`;
    chatHistory.appendChild(p);
    scrollChatToBottom();
}
function setTyping(show) {
    const chatHistory = getChatHistory();
    if (!chatHistory)
        return;
    const oldTyping = chatHistory.querySelector('.typing');
    if (oldTyping)
        oldTyping.remove();
    if (!show)
        return;
    const typing = document.createElement('p');
    typing.className = 'typing';
    chatHistory.appendChild(typing);
    scrollChatToBottom();
}
function removeActionBanner() {
    var _a;
    (_a = document.getElementById(ACTION_BANNER_ID)) === null || _a === void 0 ? void 0 : _a.remove();
}
function applyPhaseUI(phase) {
    const inputField = document.getElementById('chat-input');
    const sendButton = document.getElementById('chat-send');
    // Keep chat enabled between steps: resource ops + fiche corrections via natural language.
    const locked = false;
    if (inputField) {
        inputField.disabled = locked;
        inputField.placeholder =
            phase === 'between_steps'
                ? 'Free message (e.g. check receptor) or click Continue…'
                : phase === 'job_pending'
                    ? 'Job in progress — you can send a message or wait…'
                    : 'Type your message...';
        inputField.style.opacity = locked ? '0.5' : '1';
    }
    if (sendButton)
        sendButton.disabled = locked;
}
function setSendingLocked(locked, _phase) {
    const sendButton = document.getElementById('chat-send');
    if (sendButton) {
        sendButton.disabled = locked;
    }
    const inputField = document.getElementById('chat-input');
    if (inputField) {
        inputField.disabled = locked;
    }
}
function showActionBanner(phase, nextLabel, currentStep, snap, selectedPdb, onApprove, onLaunchPdb, onLaunchPeptide, onLaunchBindingSite, onLaunchDocking, uiLang = 'en') {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    removeActionBanner();
    const chatHistory = getChatHistory();
    if (!chatHistory)
        return;
    const en = uiLang === 'en';
    const sl = (step) => stepLabel(step, uiLang);
    const continueBtn = (label) => en
        ? `▶ Continue → ${escapeHtml(label)}`
        : `▶ Continuer → ${escapeHtml(label)}`;
    const banner = document.createElement('div');
    banner.className = 'langgraph-action-banner';
    banner.id = ACTION_BANNER_ID;
    const receptorId = snap === null || snap === void 0 ? void 0 : snap.receptor_id;
    const peptideId = snap === null || snap === void 0 ? void 0 : snap.peptide_id;
    const bindingSite = (snap === null || snap === void 0 ? void 0 : snap.binding_site) || undefined;
    const dockingId = snap === null || snap === void 0 ? void 0 : snap.docking_id;
    if (phase === 'brief_complete') {
        banner.innerHTML = `
      <span class="langgraph-action-label">${en
            ? 'All fields confirmed. Edit via chat, or launch step 2.'
            : "Tous les champs confirmés. Modifie via le chat, ou lance l'étape 2."}</span>
      <button type="button" class="langgraph-action-btn langgraph-action-btn-primary">${en ? '✓ Validate &amp; continue →' : '✓ Valider &amp; continuer →'}</button>
    `;
    }
    else if (currentStep === 'receptor_preparation' &&
        phase === 'between_steps' &&
        receptorId != null &&
        receptorId !== '') {
        // Mirror peptide banner: launch another (Off-Target) PDB or Continuer.
        const mainPdb = (typeof (snap === null || snap === void 0 ? void 0 : snap.main_pdb) === 'string' && snap.main_pdb) ||
            selectedPdb ||
            (typeof (snap === null || snap === void 0 ? void 0 : snap.selected_pdb) === 'string' ? snap.selected_pdb : '') ||
            '';
        const nextLabel = sl('binding_site');
        banner.classList.add('langgraph-action-banner-pdb');
        banner.innerHTML = `
      <span class="langgraph-action-label">${en
            ? `Launch an Off-Target PDB, continue to ${nextLabel}${mainPdb ? ` (Target: ${escapeHtml(mainPdb)})` : ''}, or stay here:`
            : `Lancer un PDB Off-Target, continuer vers ${nextLabel}${mainPdb ? ` (Target : ${escapeHtml(mainPdb)})` : ''}, ou rester ici :`}</span>
      <input
        type="text"
        id="langgraph-pdb-input"
        class="langgraph-pdb-input"
        placeholder="ex. 4DLQ"
        maxlength="8"
        value=""
        spellcheck="false"
        autocomplete="off"
      />
      <button type="button" class="langgraph-action-btn langgraph-action-btn-pdb" data-rec-act="launch">${en ? '⚗️ Launch Off-Target' : '⚗️ Lancer Off-Target'}</button>
      <button type="button" class="langgraph-action-btn langgraph-action-btn-primary" data-rec-act="continue">${continueBtn(nextLabel)}</button>
      <button type="button" class="langgraph-action-btn langgraph-action-btn-secondary" data-rec-act="stay">${en
            ? `Stay on ${escapeHtml(sl('receptor_preparation'))}`
            : `Rester à ${escapeHtml(sl('receptor_preparation'))}`}</button>
    `;
        (_a = banner.querySelector('[data-rec-act="launch"]')) === null || _a === void 0 ? void 0 : _a.addEventListener('click', () => {
            const input = banner.querySelector('#langgraph-pdb-input');
            const raw = ((input === null || input === void 0 ? void 0 : input.value) || '').trim().toUpperCase();
            if (!/^[0-9][A-Z0-9]{3}$/.test(raw)) {
                input === null || input === void 0 ? void 0 : input.focus();
                return;
            }
            removeActionBanner();
            onLaunchPdb === null || onLaunchPdb === void 0 ? void 0 : onLaunchPdb(raw);
        });
        (_b = banner.querySelector('[data-rec-act="continue"]')) === null || _b === void 0 ? void 0 : _b.addEventListener('click', () => {
            removeActionBanner();
            onApprove();
        });
        (_c = banner.querySelector('[data-rec-act="stay"]')) === null || _c === void 0 ? void 0 : _c.addEventListener('click', () => {
            // Dismiss only — stay on receptor_preparation, no launch / no advance.
            removeActionBanner();
        });
        chatHistory.appendChild(banner);
        chatHistory.scrollTop = chatHistory.scrollHeight;
        return;
    }
    else if (currentStep === 'receptor_preparation' &&
        (phase === 'job_pending' || (phase === 'between_steps' && !receptorId))) {
        // job_pending here always means "no usable receptor" (none launched yet, or
        // launch failed/stopped) — offer the PDB form regardless of receptorId so the
        // user can relaunch. between_steps + no receptorId is the pre-launch preview.
        const pdb = selectedPdb ||
            (typeof (snap === null || snap === void 0 ? void 0 : snap.selected_pdb) === 'string' ? snap.selected_pdb : '') ||
            (typeof (snap === null || snap === void 0 ? void 0 : snap.main_pdb) === 'string' ? snap.main_pdb : '');
        banner.classList.add('langgraph-action-banner-pdb');
        banner.innerHTML = `
      <span class="langgraph-action-label">${en
            ? 'PDB ID for receptor preparation (becomes Target if first):'
            : 'PDB ID pour la préparation du récepteur (Target si premier) :'}</span>
      <input
        type="text"
        id="langgraph-pdb-input"
        class="langgraph-pdb-input"
        placeholder="ex. 1MFG"
        maxlength="4"
        value="${escapeHtml(pdb)}"
        spellcheck="false"
        autocomplete="off"
      />
      <button type="button" class="langgraph-action-btn langgraph-action-btn-pdb">${en ? '⚗️ Launch preparation' : '⚗️ Lancer la préparation'}</button>
    `;
        (_d = banner.querySelector('.langgraph-action-btn-pdb')) === null || _d === void 0 ? void 0 : _d.addEventListener('click', () => {
            const input = banner.querySelector('#langgraph-pdb-input');
            const raw = ((input === null || input === void 0 ? void 0 : input.value) || pdb || '').trim().toUpperCase();
            if (!raw || raw.length < 4) {
                input === null || input === void 0 ? void 0 : input.focus();
                return;
            }
            removeActionBanner();
            onLaunchPdb === null || onLaunchPdb === void 0 ? void 0 : onLaunchPdb(raw);
        });
        chatHistory.appendChild(banner);
        scrollChatToBottom();
        return;
    }
    else if (currentStep === 'peptide_design' &&
        phase === 'between_steps') {
        const hasPeptide = peptideId != null && peptideId !== '';
        const pendingSeq = (typeof (snap === null || snap === void 0 ? void 0 : snap.pending_peptide_sequence) === 'string' && snap.pending_peptide_sequence.trim()) ||
            '';
        // Prefill for edit/relaunch; leave empty when launching another peptide so the user can type.
        const initialSeq = hasPeptide ? '' : pendingSeq;
        const peptidePlaceholder = escapeHtml(getPanelPeptidePlaceholder());
        banner.classList.add('langgraph-action-banner-pdb', 'langgraph-action-banner-peptide');
        banner.innerHTML = `
      <span class="langgraph-action-label">${hasPeptide
            ? (en
                ? `Launch another peptide, or continue to ${sl('docking')}:`
                : `Lancer un autre peptide, ou continuer vers ${sl('docking')} :`)
            : (en
                ? 'Peptide sequence for CPBuilder:'
                : 'Séquence peptidique pour CPBuilder :')}</span>
      <textarea
        id="langgraph-peptide-input"
        class="langgraph-peptide-sequence-input"
        placeholder="${peptidePlaceholder}"
        maxlength="300"
        rows="2"
        spellcheck="false"
        autocomplete="off"
      >${escapeHtml(initialSeq)}</textarea>
      <button type="button" class="langgraph-action-btn langgraph-action-btn-pdb" data-peptide-act="launch">${hasPeptide
            ? (en ? '🧬 Launch another peptide' : '🧬 Lancer un autre peptide')
            : (en ? '🧬 Launch peptide' : '🧬 Lancer le peptide')}</button>
      ${hasPeptide
            ? `<button type="button" class="langgraph-action-btn langgraph-action-btn-primary" data-peptide-act="continue">${continueBtn(sl('docking'))}</button>`
            : ''}
`;
        const peptideEl = banner.querySelector('#langgraph-peptide-input');
        if (peptideEl) {
            attachPeptideSpaceToDash(peptideEl);
            peptideEl.removeAttribute('readonly');
            peptideEl.removeAttribute('disabled');
        }
        (_e = banner.querySelector('[data-peptide-act="launch"]')) === null || _e === void 0 ? void 0 : _e.addEventListener('click', () => {
            const input = banner.querySelector('#langgraph-peptide-input');
            // Same rules as left panel: non-empty after trim; spaces already → dashes.
            const raw = normalizePanelPeptideSequence((input === null || input === void 0 ? void 0 : input.value) || '');
            if (!raw) {
                input === null || input === void 0 ? void 0 : input.focus();
                return;
            }
            removeActionBanner();
            onLaunchPeptide === null || onLaunchPeptide === void 0 ? void 0 : onLaunchPeptide(raw);
        });
        (_f = banner.querySelector('[data-peptide-act="continue"]')) === null || _f === void 0 ? void 0 : _f.addEventListener('click', () => {
            removeActionBanner();
            onApprove();
        });
        chatHistory.appendChild(banner);
        scrollChatToBottom();
        return;
    }
    else if (currentStep === 'binding_site' &&
        phase === 'between_steps' &&
        !bindingSite) {
        const totalRaw = snap === null || snap === void 0 ? void 0 : snap.available_binding_site_count;
        const total = typeof totalRaw === 'number' && totalRaw > 0 ? totalRaw : 0;
        // Hide select banner while receptor/pockets are not ready yet (avoid 1…N fake range).
        if (total <= 0) {
            return;
        }
        const rangeLabel = `1…${total}`;
        banner.classList.add('langgraph-action-banner-pdb');
        banner.innerHTML = `
      <span class="langgraph-action-label">${en ? `Site number (${rangeLabel}):` : `Numéro du site (${rangeLabel}) :`}</span>
      <input
        type="text"
        id="langgraph-binding-input"
        class="langgraph-peptide-input"
        placeholder="${en ? 'e.g. 1' : 'ex. 1'}"
        maxlength="12"
        value="1"
        spellcheck="false"
        autocomplete="off"
      />
      <button type="button" class="langgraph-action-btn langgraph-action-btn-pdb">${en ? '🎯 Select site' : '🎯 Sélectionner le site'}</button>
    `;
        (_g = banner.querySelector('.langgraph-action-btn-pdb')) === null || _g === void 0 ? void 0 : _g.addEventListener('click', () => {
            const input = banner.querySelector('#langgraph-binding-input');
            const raw = ((input === null || input === void 0 ? void 0 : input.value) || '').trim();
            if (!raw) {
                input === null || input === void 0 ? void 0 : input.focus();
                return;
            }
            removeActionBanner();
            onLaunchBindingSite === null || onLaunchBindingSite === void 0 ? void 0 : onLaunchBindingSite(raw);
        });
        chatHistory.appendChild(banner);
        scrollChatToBottom();
        return;
    }
    else if (currentStep === 'screening' &&
        phase === 'between_steps') {
        const site = (snap === null || snap === void 0 ? void 0 : snap.binding_site) || undefined;
        const hasScreening = (snap === null || snap === void 0 ? void 0 : snap.screening_id) != null && snap.screening_id !== '';
        const pdb = (typeof (snap === null || snap === void 0 ? void 0 : snap.selected_pdb) === 'string' && snap.selected_pdb) ||
            (typeof (site === null || site === void 0 ? void 0 : site.pdb_id) === 'string' ? site.pdb_id : '') ||
            '?';
        const bs = (site === null || site === void 0 ? void 0 : site.site_index) != null && site.site_index !== ''
            ? String(site.site_index === 0 ? 1 : site.site_index)
            : '1';
        const totalRaw = (_h = snap === null || snap === void 0 ? void 0 : snap.available_binding_site_count) !== null && _h !== void 0 ? _h : site === null || site === void 0 ? void 0 : site.n_binding_sites;
        const total = typeof totalRaw === 'number' && totalRaw > 0 ? totalRaw : 0;
        const rangeHint = total > 0 ? ` (1…${total})` : '';
        banner.classList.add('langgraph-action-banner-pdb');
        banner.innerHTML = `
      <span class="langgraph-action-label">${hasScreening
            ? (en
                ? `Launch another screening, or continue to ${sl('peptide_design')}:`
                : `Lancer un autre screening, ou continuer vers ${sl('peptide_design')} :`)
            : (en
                ? `PDB <strong>${escapeHtml(pdb)}</strong> · binding site${escapeHtml(rangeHint)}:`
                : `PDB <strong>${escapeHtml(pdb)}</strong> · binding site${escapeHtml(rangeHint)} :`)}</span>
      <input
        type="text"
        id="langgraph-screening-site"
        class="langgraph-pdb-input"
        placeholder="ex. 1"
        maxlength="12"
        value="${escapeHtml(bs)}"
        spellcheck="false"
        autocomplete="off"
      />
      <button type="button" class="langgraph-action-btn langgraph-action-btn-pdb" data-screen-act="launch">${hasScreening
            ? (en ? '🚀 Launch another screening' : '🚀 Lancer un autre screening')
            : (en ? '🚀 Launch screening' : '🚀 Lancer le screening')}</button>
      ${hasScreening
            ? `<button type="button" class="langgraph-action-btn langgraph-action-btn-primary" data-screen-act="continue">${continueBtn(sl('peptide_design'))}</button>`
            : `<button type="button" class="langgraph-action-btn langgraph-action-btn-secondary" data-screen-act="skip">${en ? `Skip → ${sl('peptide_design')}` : `Passer → ${sl('peptide_design')}`}</button>`}
    `;
        (_j = banner.querySelector('[data-screen-act="launch"]')) === null || _j === void 0 ? void 0 : _j.addEventListener('click', () => {
            const input = banner.querySelector('#langgraph-screening-site');
            const raw = ((input === null || input === void 0 ? void 0 : input.value) || bs || '').trim();
            if (!raw) {
                input === null || input === void 0 ? void 0 : input.focus();
                return;
            }
            removeActionBanner();
            onLaunchBindingSite === null || onLaunchBindingSite === void 0 ? void 0 : onLaunchBindingSite(`lance screening site ${raw}`);
        });
        (_k = banner.querySelector('[data-screen-act="continue"]')) === null || _k === void 0 ? void 0 : _k.addEventListener('click', () => {
            removeActionBanner();
            onApprove();
        });
        (_l = banner.querySelector('[data-screen-act="skip"]')) === null || _l === void 0 ? void 0 : _l.addEventListener('click', () => {
            removeActionBanner();
            onLaunchBindingSite === null || onLaunchBindingSite === void 0 ? void 0 : onLaunchBindingSite('passer');
        });
        chatHistory.appendChild(banner);
        scrollChatToBottom();
        return;
    }
    else if (currentStep === 'docking' && phase === 'between_steps') {
        const site = (snap === null || snap === void 0 ? void 0 : snap.binding_site) || undefined;
        const pendingJobs = Array.isArray(snap === null || snap === void 0 ? void 0 : snap.pending_jobs) ? snap.pending_jobs : [];
        let dockStatus = '';
        for (let i = pendingJobs.length - 1; i >= 0; i -= 1) {
            const job = pendingJobs[i];
            if (job && job.type === 'docking') {
                dockStatus = String(job.status || '').toLowerCase();
                break;
            }
        }
        const dockFailed = ['failed', 'job_submission_failed', 'cancelled', 'stopped', 'error'].includes(dockStatus);
        const dockDone = ['completed', 'complete', 'done', 'ok'].includes(dockStatus);
        // First launch (no id), failed job, or completed → show form (like peptide / screening).
        // Hide while a docking is still in_progress (phase stays job_pending in that case).
        if (dockingId && !dockFailed && !dockDone) {
            return;
        }
        const hasDocking = dockingId != null && dockingId !== '';
        const hintPdb = (typeof (snap === null || snap === void 0 ? void 0 : snap.selected_pdb) === 'string' && snap.selected_pdb) ||
            (typeof (site === null || site === void 0 ? void 0 : site.pdb_id) === 'string' ? site.pdb_id : '') ||
            '';
        const hintSeq = (typeof (snap === null || snap === void 0 ? void 0 : snap.pending_peptide_sequence) === 'string' && snap.pending_peptide_sequence) ||
            '';
        const hintSite = (site === null || site === void 0 ? void 0 : site.menu_index) != null && site.menu_index !== ''
            ? String(site.menu_index)
            : (site === null || site === void 0 ? void 0 : site.site_index) != null && site.site_index !== '' && site.site_index !== -1
                ? String(site.site_index)
                : '';
        const label = dockFailed
            ? (en
                ? 'Docking failed — adjust params and relaunch:'
                : 'Docking en échec — ajuste et relance :')
            : hasDocking
                ? (en
                    ? 'Launch another docking:'
                    : 'Lancer un autre docking :')
                : (en
                    ? 'Select receptor, peptide and binding site for docking:'
                    : 'Sélectionner récepteur, peptide et binding site pour le docking :');
        const launchLabel = dockFailed
            ? (en ? '🚀 Relaunch docking' : '🚀 Relancer le docking')
            : hasDocking
                ? (en ? '🚀 Launch another docking' : '🚀 Lancer un autre docking')
                : (en ? '🚀 Launch docking' : '🚀 Lancer le docking');
        banner.classList.add('langgraph-action-banner-pdb', 'langgraph-action-banner-docking');
        banner.innerHTML = `
      <span class="langgraph-action-label">${label}</span>
      <div class="langgraph-docking-fields">
        <label class="langgraph-docking-field">
          <span>${en ? 'Receptor' : 'Récepteur'}</span>
          <input
            type="text"
            id="langgraph-docking-receptor"
            class="langgraph-pdb-input"
            placeholder="${escapeHtml(hintPdb || 'ex. 1MFG')}"
            maxlength="24"
            value="${escapeHtml(hintPdb)}"
            spellcheck="false"
            autocomplete="off"
          />
        </label>
        <label class="langgraph-docking-field">
          <span>Peptide</span>
          <input
            type="text"
            id="langgraph-docking-peptide"
            class="langgraph-peptide-input"
            placeholder="${escapeHtml(hintSeq || 'ex. 1 ou ACLYFK')}"
            maxlength="80"
            value="${escapeHtml(hintSeq)}"
            spellcheck="false"
            autocomplete="off"
          />
        </label>
        <label class="langgraph-docking-field">
          <span>Site</span>
          <input
            type="text"
            id="langgraph-docking-site"
            class="langgraph-pdb-input"
            placeholder="${escapeHtml(hintSite || 'ex. 1')}"
            maxlength="12"
            value="${escapeHtml(hintSite)}"
            spellcheck="false"
            autocomplete="off"
          />
        </label>
      </div>
      <button type="button" class="langgraph-action-btn langgraph-action-btn-pdb">${launchLabel}</button>
    `;
        (_m = banner.querySelector('.langgraph-action-btn-pdb')) === null || _m === void 0 ? void 0 : _m.addEventListener('click', () => {
            const recEl = banner.querySelector('#langgraph-docking-receptor');
            const pepEl = banner.querySelector('#langgraph-docking-peptide');
            const siteEl = banner.querySelector('#langgraph-docking-site');
            const rec = ((recEl === null || recEl === void 0 ? void 0 : recEl.value) || hintPdb || '').trim();
            const pep = ((pepEl === null || pepEl === void 0 ? void 0 : pepEl.value) || hintSeq || '').trim().toUpperCase();
            const bs = ((siteEl === null || siteEl === void 0 ? void 0 : siteEl.value) || hintSite || '').trim();
            if (!rec) {
                recEl === null || recEl === void 0 ? void 0 : recEl.focus();
                return;
            }
            if (!pep) {
                pepEl === null || pepEl === void 0 ? void 0 : pepEl.focus();
                return;
            }
            if (!bs) {
                siteEl === null || siteEl === void 0 ? void 0 : siteEl.focus();
                return;
            }
            removeActionBanner();
            onLaunchDocking === null || onLaunchDocking === void 0 ? void 0 : onLaunchDocking(`lance docking receptor ${rec} peptide ${pep} binding site ${bs}`);
        });
        chatHistory.appendChild(banner);
        scrollChatToBottom();
        return;
    }
    else if (phase === 'between_steps') {
        if (currentStep === 'pdb_selection') {
            const label = nextLabel || sl('receptor_preparation') || (en ? 'Receptor Preparation' : 'Préparation récepteur');
            banner.innerHTML = `
        <span class="langgraph-action-label">${en
                ? 'PDB saved. Continue to receptor preparation to enter the PDB and launch the job.'
                : 'PDB enregistré. Passez à la préparation récepteur pour saisir le PDB et lancer le job.'}</span>
        <button type="button" class="langgraph-action-btn langgraph-action-btn-primary">${continueBtn(label)}</button>
      `;
        }
        else {
            const label = nextLabel || (currentStep ? sl(currentStep) : '') || (en ? 'next step' : 'étape suivante');
            banner.innerHTML = `
        <span class="langgraph-action-label">${en
                ? `Ready for <strong>${escapeHtml(label)}</strong>?`
                : `Prêt pour <strong>${escapeHtml(label)}</strong> ?`}</span>
        <button type="button" class="langgraph-action-btn langgraph-action-btn-primary">${continueBtn(label)}</button>
      `;
        }
    }
    else {
        return;
    }
    (_o = banner.querySelector('.langgraph-action-btn')) === null || _o === void 0 ? void 0 : _o.addEventListener('click', () => {
        removeActionBanner();
        onApprove();
    });
    chatHistory.appendChild(banner);
    scrollChatToBottom();
}
function handleChatResponse(data, onApprove, onLaunchPdb, onLaunchPeptide, onLaunchBindingSite, onLaunchDocking, lastStepFicheKeyRef) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const phase = ((_a = data.phase) !== null && _a !== void 0 ? _a : 'collecting_brief');
    chatUiLang = (_b = data.ui_lang) !== null && _b !== void 0 ? _b : 'en';
    const notices = resolveChatNotices(data);
    const reply = (_d = (_c = data.ai_response) !== null && _c !== void 0 ? _c : data.message) !== null && _d !== void 0 ? _d : '';
    const suppressBubble = shouldSuppressOperationalBubble(data, notices);
    const hasConfirmCard = ((_e = data.actions) !== null && _e !== void 0 ? _e : []).some((a) => a.action === 'navigate_to_step' ||
        a.action === 'stay_on_step' ||
        a.action === 'retry_step_launch' ||
        a.action === 'skip_failed_step' ||
        a.action === 'confirm_docking_launch' ||
        a.action === 'cancel_docking_launch');
    if (notices.length && suppressBubble) {
        const intro = noticeIntroMessage(notices, {
            currentStep: (_f = data.active_step) !== null && _f !== void 0 ? _f : data.current_step,
        });
        if (intro) {
            appendChatLine('bot', intro);
        }
        else if (reply.trim() && !hasConfirmCard) {
            // Details cards: show the conversational reply (invite free Q&A), not a duplicate headline.
            appendChatLine('bot', reply);
        }
    }
    if (notices.length)
        appendChatNotices(notices);
    // Confirm card already shows the explanation — don't duplicate as a chat bubble.
    if (reply && !suppressBubble && !hasConfirmCard)
        appendChatLine('bot', reply);
    if (shouldShowStepFiche(phase)) {
        const fiche = resolveStepFiche(data);
        if (fiche) {
            appendStepFiche(fiche);
            lastStepFicheKeyRef.current = stepFicheDedupKey(data, fiche);
        }
    }
    // Action banners (binding-site select, Continuer, …) always AFTER the bot
    // message / fiche for this turn — never before the sites list.
    if (phase === 'brief_complete' || phase === 'between_steps') {
        // Navigation confirm card replaces Continuer — don't stack both.
        const hasConfirmCard = ((_g = data.actions) !== null && _g !== void 0 ? _g : []).some((a) => a.action === 'navigate_to_step' ||
            a.action === 'stay_on_step' ||
            a.action === 'retry_step_launch' ||
            a.action === 'skip_failed_step' ||
            a.action === 'confirm_docking_launch' ||
            a.action === 'cancel_docking_launch');
        if (!hasConfirmCard) {
            showActionBanner(phase, (_h = data.next_step_label) !== null && _h !== void 0 ? _h : '', (_j = data.active_step) !== null && _j !== void 0 ? _j : data.current_step, data.state_snapshot, data.selected_pdb, onApprove, onLaunchPdb, onLaunchPeptide, onLaunchBindingSite, onLaunchDocking, (_k = data.ui_lang) !== null && _k !== void 0 ? _k : 'en');
        }
    }
    // Clickable confirmation actions (e.g. navigation) — independent from Continuer banner.
    showConfirmActions(data);
    applyPhaseUI(phase);
    return phase;
}
const CONFIRM_ACTIONS_ID = 'langgraph-confirm-actions';
function removeConfirmActions() {
    var _a;
    (_a = document.getElementById(CONFIRM_ACTIONS_ID)) === null || _a === void 0 ? void 0 : _a.remove();
}
function showConfirmActions(data) {
    var _a;
    removeConfirmActions();
    const actions = (_a = data.actions) !== null && _a !== void 0 ? _a : [];
    if (!actions.length)
        return;
    const chatHistory = getChatHistory();
    if (!chatHistory)
        return;
    const isNav = actions.some((a) => a.action === 'navigate_to_step') ||
        actions.some((a) => a.action === 'stay_on_step');
    const isLaunchRecovery = actions.some((a) => a.action === 'retry_step_launch') ||
        actions.some((a) => a.action === 'skip_failed_step');
    const isDockingConfirm = actions.some((a) => a.action === 'confirm_docking_launch') ||
        actions.some((a) => a.action === 'cancel_docking_launch');
    const lang = chatLang(data);
    const en = lang === 'en';
    const wrap = document.createElement('div');
    wrap.id = CONFIRM_ACTIONS_ID;
    if (isNav || isLaunchRecovery || isDockingConfirm) {
        wrap.className = 'langgraph-nav-confirm';
        const title = document.createElement('div');
        title.className = 'langgraph-nav-confirm-title';
        if (isLaunchRecovery) {
            title.textContent = en ? 'Launch failed' : 'Échec du lancement';
        }
        else if (isDockingConfirm) {
            title.textContent = en ? 'Confirm docking' : 'Confirmer le docking';
        }
        else {
            const locked = actions.some((a) => a.action === 'stay_on_step' && !actions.some((x) => x.action === 'navigate_to_step'));
            title.textContent = locked
                ? (en ? 'Step unavailable' : 'Étape indisponible')
                : (en ? 'Change step' : 'Changer d\'étape');
        }
        wrap.appendChild(title);
        const body = document.createElement('p');
        body.className = 'langgraph-nav-confirm-body';
        const raw = (data.ai_response || data.message || '').trim() ||
            (isLaunchRecovery
                ? (en
                    ? 'You can retry the launch, or move on to the next step.'
                    : 'Tu peux réessayer le lancement, ou passer à l\'étape suivante.')
                : isDockingConfirm
                    ? (en
                        ? 'Check PDB, sequence, and binding site before launching.'
                        : 'Vérifie le PDB, la séquence et le site de liaison avant de lancer.')
                    : (en
                        ? 'Confirm to change step, or stay on the current step.'
                        : 'Confirme pour changer d\'étape, ou reste sur l\'étape actuelle.'));
        // Keep the card concise — first two paragraphs max.
        body.textContent = raw
            .replace(/\*\*/g, '')
            .split(/\n\n+/)
            .slice(0, 2)
            .join('\n\n');
        wrap.appendChild(body);
        const btns = document.createElement('div');
        btns.className = 'langgraph-nav-confirm-actions';
        for (const action of actions) {
            if (!(action === null || action === void 0 ? void 0 : action.label))
                continue;
            const btn = document.createElement('button');
            btn.type = 'button';
            const isPrimary = action.action === 'navigate_to_step' ||
                action.action === 'retry_step_launch' ||
                action.action === 'confirm_docking_launch' ||
                action.type === 'confirm_action';
            const isSecondary = action.action === 'stay_on_step' ||
                action.action === 'skip_failed_step' ||
                action.action === 'cancel_docking_launch';
            btn.className = isSecondary
                ? 'langgraph-action-btn langgraph-action-btn-secondary'
                : isPrimary
                    ? 'langgraph-action-btn langgraph-action-btn-primary'
                    : 'langgraph-action-btn langgraph-action-btn-secondary';
            btn.textContent = action.label;
            btn.addEventListener('click', () => {
                removeConfirmActions();
                const send = window.__langGraphSendConfirm;
                send === null || send === void 0 ? void 0 : send({
                    label: action.label,
                    action: action.action,
                    type: action.type,
                });
            });
            btns.appendChild(btn);
        }
        wrap.appendChild(btns);
    }
    else {
        wrap.className = 'langgraph-action-banner';
        const label = document.createElement('span');
        label.className = 'langgraph-action-label';
        label.textContent = 'Confirm action:';
        wrap.appendChild(label);
        for (const action of actions) {
            if (!(action === null || action === void 0 ? void 0 : action.label))
                continue;
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'langgraph-action-btn langgraph-action-btn-primary';
            btn.textContent = action.label;
            btn.addEventListener('click', () => {
                removeConfirmActions();
                const send = window.__langGraphSendConfirm;
                send === null || send === void 0 ? void 0 : send({
                    label: action.label,
                    action: action.action,
                    type: action.type,
                });
            });
            wrap.appendChild(btn);
        }
    }
    chatHistory.appendChild(wrap);
    scrollChatToBottom();
}
/** Replay checkpoint messages + restore phase banner/fiche without re-sending to the graph. */
function applyThreadRestore(data, onApprove, onLaunchPdb, onLaunchPeptide, onLaunchBindingSite, onLaunchDocking, lastStepFicheKeyRef) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const chatHistory = getChatHistory();
    if (chatHistory)
        chatHistory.innerHTML = '';
    removeActionBanner();
    for (const msg of (_a = data.messages) !== null && _a !== void 0 ? _a : []) {
        const content = (msg.content || '').trim();
        // Hide machine Target-sync lines from replay (panel already applied the change).
        const hideUserSync = msg.role === 'user' &&
            (/^__sync_main_target__\b/i.test(content) ||
                /^passe le r[eé]cepteur\b.+\ben target\b/i.test(content));
        if (content && !hideUserSync)
            appendChatLine(msg.role, msg.content);
        if ((_b = msg.notices) === null || _b === void 0 ? void 0 : _b.length)
            appendChatNotices(msg.notices);
    }
    const chat = data.chat;
    if (!chat) {
        applyPhaseUI('collecting_brief');
        return 'collecting_brief';
    }
    chatUiLang = (_c = chat.ui_lang) !== null && _c !== void 0 ? _c : 'en';
    const phase = ((_d = chat.phase) !== null && _d !== void 0 ? _d : 'collecting_brief');
    if (shouldShowStepFiche(phase)) {
        const fiche = resolveStepFiche(chat);
        if (fiche) {
            appendStepFiche(fiche);
            lastStepFicheKeyRef.current = stepFicheDedupKey(chat, fiche);
        }
    }
    if (phase === 'brief_complete' || phase === 'between_steps' || (phase === 'job_pending' && chat.current_step === 'receptor_preparation')) {
        const hasConfirmCard = ((_e = chat.actions) !== null && _e !== void 0 ? _e : []).some((a) => a.action === 'navigate_to_step' ||
            a.action === 'stay_on_step' ||
            a.action === 'retry_step_launch' ||
            a.action === 'skip_failed_step' ||
            a.action === 'confirm_docking_launch' ||
            a.action === 'cancel_docking_launch');
        if (!hasConfirmCard) {
            showActionBanner(phase, (_f = chat.next_step_label) !== null && _f !== void 0 ? _f : '', (_g = chat.active_step) !== null && _g !== void 0 ? _g : chat.current_step, chat.state_snapshot, chat.selected_pdb, onApprove, onLaunchPdb, onLaunchPeptide, onLaunchBindingSite, onLaunchDocking, (_h = chat.ui_lang) !== null && _h !== void 0 ? _h : 'en');
        }
    }
    showConfirmActions(chat);
    applyPhaseUI(phase);
    return phase;
}
function restoreChatThread(projectId, onApprove, onLaunchPdb, onLaunchPeptide, onLaunchBindingSite, onLaunchDocking, lastStepFicheKeyRef, setLastResponse, currentPhaseRef, signal, isCurrent) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        const chatHistory = getChatHistory();
        if (!chatHistory)
            return;
        try {
            const msToken = yield getAccessTokenForOrchestrator();
            if (!msToken || signal.aborted || !isCurrent())
                return;
            const response = yield fetch(`${THREAD_URL}?project_id=${encodeURIComponent(projectId)}`, {
                headers: Object.assign(Object.assign({}, getAuthHeaders()), { Accept: 'application/json' }),
                signal,
            });
            const data = yield response.json().catch(() => ({ exists: false }));
            if (signal.aborted || !isCurrent())
                return;
            if (!response.ok || !data.exists) {
                chatHistory.innerHTML = '';
                removeActionBanner();
                currentPhaseRef.current = 'collecting_brief';
                applyPhaseUI('collecting_brief');
                setLastResponse(null);
                return;
            }
            const username = getLoggedInUsername();
            const activeId = username
                ? getActiveSessionId(username)
                : parseInt(projectId, 10);
            if (username && ((_b = (_a = data.chat) === null || _a === void 0 ? void 0 : _a.project_brief) === null || _b === void 0 ? void 0 : _b.target)) {
                updateProjectTarget(username, activeId, data.chat.project_brief.target);
            }
            setLastResponse((_c = data.chat) !== null && _c !== void 0 ? _c : null);
            const phase = applyThreadRestore(data, onApprove, onLaunchPdb, onLaunchPeptide, onLaunchBindingSite, onLaunchDocking, lastStepFicheKeyRef);
            currentPhaseRef.current = phase;
            if (data.chat) {
                yield ((_d = window.applyOrchestratorChatSync) !== null && _d !== void 0 ? _d : applyOrchestratorChatSync)(data.chat);
            }
        }
        catch (err) {
            if (signal.aborted || (err instanceof DOMException && err.name === 'AbortError'))
                return;
            console.warn('[ChatbotLangGraph] Failed to restore chat thread:', err);
            if (chatHistory)
                chatHistory.innerHTML = '';
            removeActionBanner();
            currentPhaseRef.current = 'collecting_brief';
            applyPhaseUI('collecting_brief');
            setLastResponse(null);
        }
    });
}
function clearChatHistory(projectId, setLastResponse, currentPhaseRef, lastStepFicheKeyRef) {
    return __awaiter(this, void 0, void 0, function* () {
        const showConfirm = window.showConfirmationModal;
        if (!showConfirm) {
            console.error('[ChatbotLangGraph] showConfirmationModal not available');
            return;
        }
        const confirmed = yield showConfirm(t("Effacer l'historique du chat ?", 'Clear chat history?'), t("Attention : tout l'historique de conversation sera supprimé, " +
            "avec le contexte de l'assistant. La pipeline chat sera remise à zéro : " +
            `${CLEAR_STEP1_LABEL}.`, 'Warning: the full conversation history will be deleted, including the ' +
            'assistant context. The chat pipeline will reset to ' +
            `${CLEAR_STEP1_LABEL}.`), t('Effacer', 'Clear'), t('Annuler', 'Cancel'), t('La session MexaScreen reste intacte : récepteurs, peptides, dockings, ' +
            'calculs lancés et tout le panneau de gauche sont conservés.', 'Your MexaScreen session stays intact: receptors, peptides, dockings, ' +
            'running jobs, and everything in the left panel are kept.'));
        if (!confirmed)
            return;
        const chatHistory = getChatHistory();
        setTyping(true);
        removeActionBanner();
        try {
            const msToken = yield getAccessTokenForOrchestrator();
            if (!msToken) {
                setTyping(false);
                appendChatLine('bot', t('Session expirée. Reconnectez-vous pour utiliser l’assistant.', 'Session expired. Please sign in again to use the assistant.'));
                return;
            }
            const response = yield fetch(`${THREAD_URL}?project_id=${encodeURIComponent(projectId)}`, {
                method: 'DELETE',
                headers: Object.assign(Object.assign({}, getAuthHeaders()), { Accept: 'application/json' }),
            });
            const data = yield response.json().catch(() => ({}));
            setTyping(false);
            if (!response.ok) {
                const err = data.error ||
                    data.detail ||
                    `Clear failed (${response.status})`;
                appendChatLine('bot', String(err));
                return;
            }
            if (chatHistory)
                chatHistory.innerHTML = '';
            removeActionBanner();
            lastStepFicheKeyRef.current = '';
            currentPhaseRef.current = 'collecting_brief';
            applyPhaseUI('collecting_brief');
            setLastResponse(null);
            appendChatLine('bot', t(`Historique effacé. ${CLEAR_STEP1_LABEL}.`, `Chat history cleared. ${CLEAR_STEP1_LABEL}.`));
            appendChatLine('bot', t('Quelle est la cible biologique ? (ex. EGFR, KRAS, PD-L1, CDK2, BRCA1…)', "What is the biological target you're working on? (e.g. EGFR, KRAS, PD-L1, CDK2, BRCA1…)"));
        }
        catch (err) {
            setTyping(false);
            console.error('[ChatbotLangGraph] Failed to clear chat history:', err);
            appendChatLine('bot', t("Impossible d'effacer l'historique. Réessayez.", 'Could not clear chat history. Please try again.'));
        }
    });
}
const ChatbotLangGraph = () => {
    const isMountedRef = useRef(true);
    const abortRef = useRef(null);
    const restoreAbortRef = useRef(null);
    const currentPhaseRef = useRef('collecting_brief');
    const sendMessageRef = useRef(() => __awaiter(void 0, void 0, void 0, function* () { }));
    const lastStepFicheKeyRef = useRef('');
    const [debugOpen, setDebugOpen] = useState(false);
    const [lastResponse, setLastResponse] = useState(null);
    const sendMessage = useCallback((text_1, ...args_1) => __awaiter(void 0, [text_1, ...args_1], void 0, function* (text, approved = false, confirmPending = false, silentUser = false) {
        var _a, _b, _c, _d, _e;
        if (!text || !isMountedRef.current)
            return;
        removeActionBanner();
        // Internal Continuer / OK — don't spam a tiny "continue" user bubble in the chat.
        // silentUser / panel Target sync: never show a user bubble for machine sync messages.
        const silentApprove = approved && /^(continue|continuer|ok|oui|yes|confirm|confirmer)$/i.test(text.trim());
        const isSilentTargetSync = silentUser ||
            /^__sync_main_target__\b/i.test(text.trim()) ||
            /^passe le r[eé]cepteur\b.+\ben target\b/i.test(text.trim());
        if (!silentApprove && !isSilentTargetSync) {
            appendChatLine('user', text);
        }
        const phaseBeforeSend = currentPhaseRef.current;
        setSendingLocked(true, phaseBeforeSend);
        setTyping(true);
        (_a = abortRef.current) === null || _a === void 0 ? void 0 : _a.abort();
        abortRef.current = new AbortController();
        try {
            const msToken = yield getAccessTokenForOrchestrator();
            if (!msToken) {
                setTyping(false);
                appendChatLine('bot', 'Session expired. Please sign in again to use the assistant.');
                applyPhaseUI(currentPhaseRef.current);
                return;
            }
            // Navbar activeSessionId drives LangGraph thread_id (actor_id = Cognito sub on server).
            const projectId = resolveActiveProjectId();
            const response = yield fetch(CHAT_URL, {
                method: 'POST',
                headers: Object.assign(Object.assign({}, getAuthHeaders()), { Accept: 'application/json' }),
                body: JSON.stringify({
                    user_message: text,
                    project_id: projectId,
                    ms_token: msToken,
                    approved,
                    confirm_pending: confirmPending,
                }),
                signal: abortRef.current.signal,
            });
            const data = yield response.json().catch(() => ({}));
            if (!isMountedRef.current)
                return;
            setTyping(false);
            if (!response.ok) {
                const detail = data.detail;
                const detailText = detail
                    ? typeof detail === 'string'
                        ? detail
                        : JSON.stringify(detail)
                    : '';
                appendChatLine('bot', data.error || detailText || `Chat error (${response.status})`);
                applyPhaseUI(currentPhaseRef.current);
                return;
            }
            // Panel Target promo sync: never surface a stale « Receptor preparation » card.
            // (Backend used to no-op after snapshot sync and re-echo the previous prep AI line.)
            let chatData = data;
            if (isSilentTargetSync) {
                const reply = ((_c = (_b = data.ai_response) !== null && _b !== void 0 ? _b : data.message) !== null && _c !== void 0 ? _c : '').trim();
                const looksLikeTargetSwitch = /main\s+target\s+updated|target\s+principale\s+mise\s+à\s+jour/i.test(reply);
                const pdb = (typeof data.main_pdb === 'string' && data.main_pdb.trim()) ||
                    text.trim().split(/\s+/)[2] ||
                    '';
                chatData = Object.assign(Object.assign({}, data), { chat_notices: [], ai_response: looksLikeTargetSwitch
                        ? reply
                        : pdb
                            ? `Main Target updated → **${pdb.toUpperCase()}**. Pipeline defaults were reset for the new Target.`
                            : reply, 
                    // Avoid Continuer / Off-Target banners on this machine turn.
                    phase: 'collecting_brief', step_fiche: undefined, actions: [] });
            }
            // Keep frontend active session authoritative until backend supports multi-workspace.
            const username = getLoggedInUsername();
            const activeId = username
                ? getActiveSessionId(username)
                : parseInt(projectId, 10);
            if (chatData.ms_session_id != null && chatData.ms_session_id === activeId) {
                syncLangGraphProjectId(activeId);
            }
            else if (chatData.project_id === String(activeId)) {
                syncLangGraphProjectId(activeId);
            }
            if (username && ((_d = chatData.project_brief) === null || _d === void 0 ? void 0 : _d.target)) {
                updateProjectTarget(username, activeId, chatData.project_brief.target);
            }
            setLastResponse(chatData);
            const phase = handleChatResponse(chatData, () => {
                void sendMessageRef.current('continue', true);
            }, (pdb) => {
                void sendMessageRef.current(pdb, true);
            }, (sequence) => {
                // Stay on peptide_design — do not approve/advance to docking.
                void sendMessageRef.current(sequence, false);
            }, (binding) => {
                void sendMessageRef.current(binding, true);
            }, (docking) => {
                void sendMessageRef.current(docking, true);
            }, lastStepFicheKeyRef);
            currentPhaseRef.current = phase;
            yield ((_e = window.applyOrchestratorChatSync) !== null && _e !== void 0 ? _e : applyOrchestratorChatSync)(chatData);
        }
        catch (err) {
            if (!isMountedRef.current)
                return;
            if (err instanceof DOMException && err.name === 'AbortError')
                return;
            setTyping(false);
            console.error('[ChatbotLangGraph] /chat failed:', err);
            appendChatLine('bot', 'Could not reach the assistant. Is LangGraph running on port 8000?');
            applyPhaseUI(currentPhaseRef.current);
        }
        finally {
            if (isMountedRef.current) {
                setSendingLocked(false, currentPhaseRef.current);
                if (currentPhaseRef.current !== 'between_steps') {
                    const inputField = document.getElementById('chat-input');
                    inputField === null || inputField === void 0 ? void 0 : inputField.focus();
                }
            }
        }
    }), []);
    sendMessageRef.current = sendMessage;
    window.__langGraphSendConfirm = (payload) => {
        const isDismiss = payload.action === 'stay_on_step' ||
            payload.action === 'cancel_docking_launch' ||
            payload.type === 'dismiss_action';
        if (isDismiss) {
            // Clear the proposal without advancing the pipeline.
            void sendMessageRef.current(payload.label, false, false);
            return;
        }
        void sendMessageRef.current(payload.label, true, true);
    };
    useEffect(() => {
        isMountedRef.current = true;
        const chatHistory = getChatHistory();
        if (chatHistory)
            chatHistory.innerHTML = '';
        const debugBtn = document.getElementById('chat-pipeline-debug-btn');
        if (debugBtn)
            debugBtn.style.display = '';
        const clearBtn = document.getElementById('chat-clear-history-btn');
        if (clearBtn)
            clearBtn.style.display = '';
        const onApprove = () => {
            void sendMessageRef.current('continue', true);
        };
        const onLaunchPdb = (pdb) => {
            // approved=false: stay on receptor_preparation and actually run prepare_receptor.
            // approved=true clears awaiting and the router skips past prep into binding_site,
            // which only redirects with a "launch Off-Target" message — no job is created.
            void sendMessageRef.current(pdb, false);
        };
        const onLaunchPeptide = (sequence) => {
            // approved=false: stay on peptide_design and re-run the node.
            // approved=true would clear awaiting and skip to docking when a peptide_id already exists.
            void sendMessageRef.current(sequence, false);
        };
        const onLaunchBindingSite = (value) => {
            void sendMessageRef.current(value, true);
        };
        const onLaunchDocking = (value) => {
            void sendMessageRef.current(value, true);
        };
        const restoreProjectId = resolveActiveProjectId();
        const restoreController = new AbortController();
        restoreAbortRef.current = restoreController;
        void restoreChatThread(restoreProjectId, onApprove, onLaunchPdb, onLaunchPeptide, onLaunchBindingSite, onLaunchDocking, lastStepFicheKeyRef, setLastResponse, currentPhaseRef, restoreController.signal, () => isMountedRef.current && resolveActiveProjectId() === restoreProjectId);
        const handleSend = () => {
            const inputField = document.getElementById('chat-input');
            const text = inputField === null || inputField === void 0 ? void 0 : inputField.value.trim();
            if (!inputField || !text || inputField.disabled)
                return;
            inputField.value = '';
            void sendMessageRef.current(text, false);
        };
        const handleKeyDown = (e) => {
            if (e.key !== 'Enter' || e.shiftKey || e.isComposing)
                return;
            e.preventDefault();
            e.stopPropagation();
            handleSend();
        };
        const handleClearHistory = () => {
            void clearChatHistory(resolveActiveProjectId(), setLastResponse, currentPhaseRef, lastStepFicheKeyRef);
        };
        const sendButton = document.getElementById('chat-send');
        const inputField = document.getElementById('chat-input');
        sendButton === null || sendButton === void 0 ? void 0 : sendButton.addEventListener('click', handleSend);
        inputField === null || inputField === void 0 ? void 0 : inputField.addEventListener('keydown', handleKeyDown);
        clearBtn === null || clearBtn === void 0 ? void 0 : clearBtn.addEventListener('click', handleClearHistory);
        const onDebugClick = () => setDebugOpen(true);
        debugBtn === null || debugBtn === void 0 ? void 0 : debugBtn.addEventListener('click', onDebugClick);
        const unsubscribeAuth = onAuthStateChange((_user, authenticated) => {
            if (!authenticated)
                setDebugOpen(false);
        });
        const onMainTargetChanged = (ev) => {
            const detail = ev.detail || {};
            const rid = detail.receptorID;
            const pdb = typeof detail.pdbID === 'string' ? detail.pdbID.trim().toUpperCase() : '';
            if (rid == null)
                return;
            // Machine sync only — never a user-visible chat line. Panel already wrote DynamoDB.
            const msg = pdb
                ? `__sync_main_target__ ${rid} ${pdb}`
                : `__sync_main_target__ ${rid}`;
            void sendMessageRef.current(msg, false, false, true);
        };
        window.addEventListener('mexa:main-target-changed', onMainTargetChanged);
        return () => {
            var _a, _b;
            setDebugOpen(false);
            unsubscribeAuth();
            isMountedRef.current = false;
            (_a = abortRef.current) === null || _a === void 0 ? void 0 : _a.abort();
            (_b = restoreAbortRef.current) === null || _b === void 0 ? void 0 : _b.abort();
            sendButton === null || sendButton === void 0 ? void 0 : sendButton.removeEventListener('click', handleSend);
            inputField === null || inputField === void 0 ? void 0 : inputField.removeEventListener('keydown', handleKeyDown);
            clearBtn === null || clearBtn === void 0 ? void 0 : clearBtn.removeEventListener('click', handleClearHistory);
            debugBtn === null || debugBtn === void 0 ? void 0 : debugBtn.removeEventListener('click', onDebugClick);
            window.removeEventListener('mexa:main-target-changed', onMainTargetChanged);
            if (debugBtn)
                debugBtn.style.display = 'none';
            if (clearBtn)
                clearBtn.style.display = 'none';
            removeActionBanner();
        };
    }, []);
    return createPortal(React.createElement(PipelineDebugDrawer, { open: debugOpen, onClose: () => setDebugOpen(false), data: lastResponse }), document.body);
};
export default ChatbotLangGraph;
